import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { AppointmentsHttpService, PlanningDetailsResponse, PrescriptionPlanningStatus, PrescriptionsService, mapPlanningToAppointmentStatus } from '@lib/prescriptions/data-access';
import { AppointmentStatusControlComponent } from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, of, retry, switchMap, take, tap } from 'rxjs';

import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { ActionCommentRequestBody, ActionResponseBody, ActionType, EntityType } from '@lib/core/models/action.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { Task, TaskStatus, TaskType } from '../../../core/models/task.interface';
import * as TaskActions from '../../../core/store/task.actions';
import { TaskDataTargetComponent } from '../../commons/task-data-target/task-data-target.component';

@Component({
  selector: 'app-check-planned-appointment-task-details',
  standalone: true,
  imports: [MatIcon, MatFormField, MatLabel, MatInput, ReactiveFormsModule, TaskDataTargetComponent, TranslatePipe, AppointmentStatusControlComponent, LoaderComponent],
  templateUrl: './check-planned-appointment-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-6' },
})
export class CheckPlannedAppointmentTaskDetailsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly appointmentsHttpService = inject(AppointmentsHttpService);

  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIcon('doctor', sanitizer.bypassSecurityTrustResourceUrl('assets/icons/ic_doctor.svg'));
  }

  protected readonly task = inject<Task<TaskType.CheckPlannedAppointment>>(TASK_DATA);
  protected readonly formatDate = formatDate;
  protected readonly taskStatus = TaskStatus;

  protected readonly planningDetails = signal<PlanningDetailsResponse | null>(null);
  protected readonly existingNote = signal<ActionResponseBody | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly isStatusChanged = signal<boolean>(false);

  private baseNoteContent = '';

  protected readonly statusFc = new FormControl<PrescriptionPlanningStatus>(null);
  protected readonly noteFc = new FormControl<string>('');

  ngOnInit(): void {
    this.loadPlanningDetails();
    this.watchStatusChanges();
  }

  private watchStatusChanges(): void {
    this.statusFc.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const changed = value !== this.planningDetails()?.status;
      this.isStatusChanged.set(changed);

      if (changed) {
        const statusLabel = this.translate.instant('status.prescription.planning.' + value);
        const appendText = this.translate.instant('task.message.appointmentNoteStatusUpdate', { status: statusLabel });
        const updatedContent = this.baseNoteContent ? `${this.baseNoteContent}\n${appendText}` : appendText;
        this.noteFc.setValue(updatedContent, { emitEvent: false });
      } else {
        this.noteFc.setValue(this.baseNoteContent, { emitEvent: false });
      }
    });
  }

  private loadPlanningDetails(): void {
    const { prescriptionId, planningId, appointmentId } = this.task.data;

    if (!prescriptionId || !planningId) {
      this.resolveFromPrescription(prescriptionId, appointmentId);
      return;
    }

    this.fetchPlanningDetails(prescriptionId, planningId);
  }

  private resolveFromPrescription(prescriptionId: string, appointmentId: string): void {
    if (!prescriptionId || !appointmentId) {
      this.loading.set(false);
      return;
    }

    this.prescriptionsService
      .getPrescriptionById(prescriptionId)
      .pipe(
        retry(3),
        map(prescription => prescription.planning?.find(p => p.appointmentId === appointmentId)?.id ?? null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: resolvedPlanningId => {
          if (resolvedPlanningId) {
            this.fetchPlanningDetails(prescriptionId, resolvedPlanningId);
          } else {
            this.loading.set(false);
          }
        },
        error: () => this.loading.set(false),
      });
  }

  private fetchPlanningDetails(prescriptionId: string, planningId: string): void {
    this.appointmentsHttpService
      .getPlanningDetails(prescriptionId, planningId)
      .pipe(
        tap(details => {
          this.planningDetails.set(details);
          this.statusFc.setValue(details.status, { emitEvent: false });
          if (this.task.status === TaskStatus.Resolved) {
            this.statusFc.disable({ emitEvent: false });
          }
        }),
        switchMap(details => {
          if (!details.appointmentId) return of(null);

          return this.appointmentsHttpService.searchAppointmentNote(details.appointmentId).pipe(map(response => response.value[0]));
        }),
        take(1)
      )
      .subscribe({
        next: note => {
          if (note && note?.status !== 'Undefined') {
            this.existingNote.set(note);
            this.baseNoteContent = note.content ?? '';
            this.noteFc.setValue(note.content, { emitEvent: false });
          }
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected submit(): void {
    const newStatus = this.statusFc.value;
    const originalStatus = this.planningDetails()?.status;

    if (newStatus === originalStatus) return;

    this.loading.set(true);
    this.errorMessage.set(null);

    const statusUpdate$ =
      newStatus === PrescriptionPlanningStatus.Cancelled
        ? this.appointmentsHttpService.cancelAppointment(this.task.data.prescriptionId, this.planningDetails().planningId)
        : this.appointmentsHttpService.updateAppointmentStatus(this.planningDetails().appointmentId, mapPlanningToAppointmentStatus(newStatus));

    statusUpdate$
      .pipe(
        switchMap(() => this.saveNote()),
        take(1)
      )
      .subscribe({
        next: () => this.onSubmitSuccess(newStatus),
        error: err => this.onSubmitError(err),
      });
  }

  private saveNote() {
    const content = this.noteFc.value ?? '';
    const appointmentId = this.planningDetails()?.appointmentId;

    if (this.existingNote()) {
      return this.appointmentsHttpService.updateAppointmentNote(this.existingNote().id, content);
    }

    if (!appointmentId) {
      throw new Error('Appointment ID is required to create an appointment note.');
    }

    const body: ActionCommentRequestBody = {
      type: ActionType.AppointmentNote,
      entityType: EntityType.Appointment,
      entityId: appointmentId,
      content,
    };

    return this.appointmentsHttpService.createAppointmentNote(body);
  }

  private onSubmitSuccess(newStatus: PrescriptionPlanningStatus): void {
    const statusLabel = this.translate.instant('status.prescription.planning.' + newStatus);
    const resolution = this.translate.instant('task.message.appointmentStatusChanged', { status: statusLabel });
    this.store.dispatch(TaskActions.resolveTask({ task: this.task, payload: { resolution } }));
    this.router.navigate(['/tasks']);
  }

  private onSubmitError(err: any): void {
    this.loading.set(false);
    this.errorMessage.set(err?.error?.Message ?? null);
  }

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }

  protected navigateToAppointment(): void {
    this.router.navigate(['/members', this.task.data.memberId, 'prescriptions', this.task.data.prescriptionId, 'plannings', this.task.data.planningId]);
  }
}
